import React, {useState, useEffect} from 'react'
import ReactSlider from 'react-slider'
import PrimaryButton from '../common/buttons/PrimaryButton'

type Props = {
  isOpen: boolean
  onClose: () => void
  rowData: {
    systolic?: string
    diastolic?: string
    severity: string
    category: string
    systolicId?: number
    diastolicId?: number
  } | null
  onSave: (values: {
    systolic: {id: number; min: number; max: number}
    diastolic: {id: number; min: number; max: number}
  }) => void
}

const parseRange = (val?: string): [number, number] => {
  if (!val || !val.includes('-')) return [0, 0]
  const [min, max] = val.split('-').map(Number)
  return [min, max]
}

const CriticalitySettingsModal: React.FC<Props> = ({isOpen, onClose, rowData, onSave}) => {
  const [systolicRange, setSystolicRange] = useState<[number, number]>([0, 0])
  const [diastolicRange, setDiastolicRange] = useState<[number, number]>([0, 0])

  // Reset ranges when modal opens or rowData changes
  useEffect(() => {
    if (rowData) {
      setSystolicRange(parseRange(rowData.systolic))
      setDiastolicRange(parseRange(rowData.diastolic))
    }
  }, [rowData])

  const handleSave = () => {
    onSave({
      systolic: {id: rowData?.systolicId!, min: systolicRange[0], max: systolicRange[1]},
      diastolic: {id: rowData?.diastolicId!, min: diastolicRange[0], max: diastolicRange[1]},
    })
    onClose()
  }

  const handleClose = () => onClose()

  if (!rowData) return null

  return (
    <>
      {isOpen && (
        <div
          onClick={handleClose}
          className='modal-backdrop show'
          style={{backgroundColor: 'rgba(0, 0, 0, 0.5)'}}
        />
      )}

      <div className={`modal fade ${isOpen ? 'show d-block' : ''}`} tabIndex={-1}>
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>
                {rowData.category} (Severity {rowData.severity})
              </h5>
              <button type='button' className='btn-close' onClick={handleClose}></button>
            </div>

            <div className='modal-body'>
              <div className='mb-4'>
                <label className='form-label'>
                  Systolic Range:{' '}
                  <strong>
                    {systolicRange[0]} - {systolicRange[1]}
                  </strong>
                </label>
                <ReactSlider
                  className='custom-slider'
                  thumbClassName='custom-thumb'
                  value={systolicRange}
                  onChange={(val) => setSystolicRange(val as [number, number])}
                  min={0}
                  max={250}
                  step={1}
                  pearling
                  minDistance={1}
                  withTracks
                  renderTrack={(props) => (
                    <div
                      {...props}
                      style={{
                        ...props.style,
                        height: '100%',
                        background: '#dee2e6',
                        borderRadius: '4px',
                      }}
                    />
                  )}
                  renderThumb={(props: any, state: any) => (
                    <div {...props} className='custom-thumb'>
                      {state.valueNow}
                    </div>
                  )}
                />
              </div>

              <div className='mb-4'>
                <label className='form-label'>
                  Diastolic Range:{' '}
                  <strong>
                    {diastolicRange[0]} - {diastolicRange[1]}
                  </strong>
                </label>
                <ReactSlider
                  className='custom-slider'
                  thumbClassName='custom-thumb'
                  value={diastolicRange}
                  onChange={(val) => setDiastolicRange(val as [number, number])}
                  min={0}
                  max={220}
                  step={1}
                  pearling
                  minDistance={1}
                  withTracks
                  renderThumb={(props: any, state: any) => (
                    <div {...props} className='custom-thumb'>
                      {state.valueNow}
                    </div>
                  )}
                  renderTrack={(props) => (
                    <div
                      {...props}
                      style={{
                        ...props.style,
                        height: '100%',
                        background: '#dee2e6',
                        borderRadius: '4px',
                      }}
                    />
                  )}
                />
              </div>
            </div>

            <div className='modal-footer'>
              <PrimaryButton
              color='secondary'
              onClick={handleClose}>
                Cancel
              </PrimaryButton>
                <PrimaryButton onClick={handleSave}>Save</PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CriticalitySettingsModal
